import React, { useEffect, useState } from 'react';
import { PageLayout } from '@src/common/PageLayout';
import { Button } from '@src/common/Button';
import { styled } from '@uhg-abyss/web/tools/styled';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useSteps } from '@src/state/steps';
import { event as TaggingEvent, event } from '@uhg-abyss/web/tools/event';
import { useUser } from '@src/state/user/hooks';
import isEmpty from 'lodash/isEmpty';
import {
  isCliaValid,
  getSessionRequestObj,
} from '@src/state/tinRegistration/utils';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { useTinRegistration } from '@src/state/tinRegistration';
import { Loading } from '@src/common/Loading';
import { storage } from '@uhg-abyss/web/tools/storage';
import { useTinDrafts } from '@src/state/superUserDraft';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { TinHeader } from '../TinHeader';
import { PracticeInfoComponent } from './PracticeInfoComponent';
import { NPIValidationsModel } from './NPIValidations/NPIValidations';

const PageButton = styled('div', {
  width: '11%',
  marginTop: '2em',
  paddingBottom: '25px',
  fontWeight: 600,
  fontSize: '16px',
  color: '$primary1',
  fontFamily: '$heading',
  display: 'inline-flex',
  marginRight: '15px',
});

const PageDraftButton = styled('div', {
  width: 'fitContent',
  marginTop: '2em',
  paddingBottom: '25px',
  float: 'right',
  color: '$primary1',
});

const PageDraftButtonFont = styled('div', {
  fontSize: '16px',
  fontWeight: 600,
  fontFamily: '$heading',
});

const SaveDraftSuccessMessage = styled('div', {
  fontSize: '13px',
  color: '$success1',
  textAlign: 'center',
});

const SaveDraftFailedMessage = styled('div', {
  fontSize: '13px',
  textAlign: 'center',
  color: '$error1',
});

export const PracticeInformation = () => {
  const abyssForm = useForm();
  const registration = abyssForm.getValues('searchRegistration');
  const practiceType = abyssForm.getValues(
    'practiceInformation.practiceTypeSelection'
  );
  const w9Form = abyssForm.getValues('w9form');
  const practiceInfo = abyssForm.getValues('practiceInformation');
  const address = abyssForm.getValues('address');
  const signatureType = abyssForm.getValues('attestation.signatureRadio');
  const attestation = abyssForm.getValues('attestation');
  const { documentIds, trackingNumber, tinDraft, isDraftDisabled } =
    useTinRegistration.state();
  const { saveAsDraft, validateNPI } = useTinRegistration.actions();
  const { validateNPISaga } = useTinRegistration.state();

  const userInfo = useUser.state();
  const {
    pagesVisited,
    pagesSubmitted,
    setStep,
    addPageVisited,
    addPageSubmitted,
    removeSubmittedPage,
  } = useSteps();
  const isSuperUser = storage.local.get(userInfo.uuid);
  const { saveSuperUserDraft } = useTinDrafts.actions();

  const router = useRouter();

  const [isDataLoading, setDataLoading] = useState(false);
  const [isNPIValidationsModalOpen, setNPIValidationsModalOpen] =
    useState(false);

  useEffect(() => {
    const pageName = 'epvsecure:tin registration:step2-practice data';
    const billingTin = registration ? registration.billingTin : '';
    const siteSectionLevel = {
      siteSectionLevel1: 'tin registration',
      siteSectionLevel2: 'step2-practice data',
      siteSectionLevel3: '',
      siteSectionLevel4: '',
      siteSectionLevel5: '',
    };
    TaggingEvent('ON_PAGE_LOAD', {
      userInfo,
      pageName,
      billingTin,
      siteSectionLevel,
    });
    if (pagesVisited) {
      if (!pagesVisited.includes('PracticeInformation')) {
        const pages = pagesVisited.concat('PracticeInformation');
        addPageVisited(pages);
      }
    } else {
      const pages = ['PracticeInformation'];
      addPageVisited(pages);
    }
    setStep(2);
    if (
      tinDraft &&
      tinDraft.w9Details &&
      tinDraft.returnToRegistration &&
      tinDraft.practiceInformation &&
      !pagesSubmitted.includes('PracticeInformation')
    ) {
      setDataLoading(true);
      abyssForm.validate('practiceInformation', (values) => {
        if (pagesSubmitted) {
          if (!pagesSubmitted.includes('PracticeInformation')) {
            const pages = pagesSubmitted.concat('PracticeInformation');
            addPageSubmitted(pages);
          }
        } else {
          const pages = ['PracticeInformation'];
          addPageSubmitted(pages);
        }
        router.navigate('/tin-registration/step/3');
      });
      setDataLoading(false);
    }
  }, []);

  const continueRegistration = () => {
    abyssForm.validate(
      'practiceInformation',
      (values) => {
        const tNum = trackingNumber;
        const provider = values.registrationTypeSelection === 'Individual';
        const npis = values.npiInformation.split(',');
        if (pagesSubmitted) {
          if (!pagesSubmitted.includes('PracticeInformation')) {
            const pages = pagesSubmitted.concat('PracticeInformation');
            addPageSubmitted(pages);
          }
        } else {
          const pages = ['PracticeInformation'];
          addPageSubmitted(pages);
        }
        TaggingEvent('PRACTICE_DATA_CONTINUE');
        validateNPI(
          {
            isProvider: provider,
            npi: npis[0],
            trackingNumber: tNum,
          },
          (res) => {
            abyssForm.setValue('practiceInformation.isNpiValid', true);
            router.navigate('/tin-registration/step/3');
            setNPIValidationsModalOpen(false);
          },
          (error) => {
            event('API_TRANSACTION_ERROR', {
              code: error.faultCode,
              message: error.message,
            });
            setNPIValidationsModalOpen(true);
          }
        );
        const eventData = {
          action: 'continue',
        };
        TaggingEvent('EAPM_TRANSACTION_CONTINUE', eventData);
      },
      (error) => {
        const fields = {};
        if (isEmpty(error.registrationTypeSelection)) {
          fields.registrationTypeSelection = '';
        }
        if (isEmpty(error.practiceTypeSelection)) {
          fields.practiceTypeSelection = '';
        }
        if (isEmpty(error.restrictionSelection)) {
          fields.restrictionSelection = '';
        }
        if (isEmpty(error.npiInformation)) {
          fields.npiInformation = '';
        }

        fields.isNpiValid = null;

        if (!isEmpty(fields)) {
          TaggingEvent('FORM_VALIDATION_ERROR', { fields });
        }
      }
    );
  };

  const isMedicareValid = (values) => {
    let isEmptyValue = false;
    if (practiceType === 'Ambulatory Surgery Center') {
      if (!values.medicareId && !values.medicareAttestation) {
        isEmptyValue = true;
      }
    }
    return isEmptyValue;
  };

  const checkEmptyValue = (values) => {
    let emptyValue = false;
    Object.keys(values).forEach((key) => {
      if (!values[key] && !emptyValue) {
        if (
          key === 'restrictionInput' &&
          values.restrictionSelection === 'No'
        ) {
          emptyValue = false;
        } else if (key === 'medicareAttestation' || key === 'medicareId') {
          emptyValue = isMedicareValid(values);
        } else if (
          key === 'cliaFile' ||
          key === 'cliaIdentification' ||
          key === 'cliaAttestation'
        ) {
          emptyValue = isCliaValid(values);
        } else if (key === 'isNpiValid') {
          emptyValue = false;
        } else {
          emptyValue = true;
        }
      }
    });
    return emptyValue;
  };

  const goBack = () => {
    abyssForm.validate(
      'practiceInformation',
      (values) => {
        const emptyValue = checkEmptyValue(values);
        if (emptyValue) {
          const updatedSubmittedList = pagesSubmitted.filter((item) => {
            return item !== 'PracticeInformation';
          });
          removeSubmittedPage(updatedSubmittedList);
        }
        TaggingEvent('PRACTICE_DATA_BACK');
        router.navigate('/tin-registration/step/1');
      },
      (error) => {
        const fields = {};
        if (isEmpty(error.registrationTypeSelection)) {
          fields.registrationTypeSelection = '';
        }
        if (isEmpty(error.practiceTypeSelection)) {
          fields.practiceTypeSelection = '';
        }
        if (isEmpty(error.restrictionSelection)) {
          fields.restrictionSelection = '';
        }
        if (isEmpty(error.npiInformation)) {
          fields.npiInformation = '';
        }

        fields.isNpiValid = null;

        if (!isEmpty(fields)) {
          TaggingEvent('FORM_VALIDATION_ERROR', { fields });
        }
      }
    );
  };

  const [saveDraftFailed, setSaveDraftFailed] = useState(false);
  const [saveDraftSuccess, setSaveDraftSuccess] = useState(false);

  const closeNPIValidations = () => {
    setNPIValidationsModalOpen(false);
  };

  const saveDraft = () => {
    const reqBody = getSessionRequestObj({
      w9Form,
      practiceInfo,
      address,
      attestation,
      documentIds,
      signatureType,
    });

    const interactionName = 'Practice data widget';
    const interactionValue = 'save as draft';
    const eventInteraction = 'interaction';
    TaggingEvent('SAVE_AS_DRAFT', {
      eventInteraction,
      interactionName,
      interactionValue,
    });

    const billingTin = registration ? registration.billingTin : '';
    const requestId = registration ? registration.requestId : '';
    const tNum = trackingNumber;

    setSaveDraftSuccess(false);
    setSaveDraftFailed(false);

    if (!isSuperUser) {
      saveAsDraft(
        {
          billingTin,
          requestId,
          trackingNumber: tNum,
          ...reqBody,
        },
        (response) => {
          setSaveDraftSuccess(true);
        },
        (error) => {
          setSaveDraftFailed(true);
        }
      );
    } else {
      saveSuperUserDraft(
        {
          billingTin,
          requestId,
          optumId: registration.optumId,
          trackingNumber: tNum,
          ...reqBody,
        },
        (response) => {
          setSaveDraftSuccess(true);
        },
        (error) => {
          setSaveDraftFailed(true);
        }
      );
    }
  };

  return (
    <Loading isLoading={isDataLoading || validateNPISaga.isLoading}>
      <PageLayout>
        <TinHeader />
        <PracticeInfoComponent />
        <NPIValidationsModel
          isOpen={isNPIValidationsModalOpen}
          closeModal={closeNPIValidations}
        />
        <PageButton>
          <Layout.Group>
            <Button
              type="button"
              id="practice-information-back-button"
              aria-label="click here to go back"
              data-testid="start-button"
              onClick={goBack}
              width="100%"
              variant="outline"
            >
              Back
            </Button>
            <Button
              type="button"
              id="practice-information-continue-button"
              aria-label="click here to continue"
              data-testid="start-button"
              onClick={continueRegistration}
              width="100%"
            >
              Continue
            </Button>
          </Layout.Group>
        </PageButton>
        <PageDraftButton>
          <PageDraftButtonFont>
            <Button
              type="button"
              id="draft-continue-button"
              aria-label="click here to save as draft"
              data-testid="draft-button"
              onClick={saveDraft}
              width="100%"
              variant="outline"
              isDisabled={isDraftDisabled}
            >
              Save as draft
            </Button>
          </PageDraftButtonFont>
          {saveDraftSuccess && (
            <SaveDraftSuccessMessage id="savedraft-success">
              Saved successfully.
            </SaveDraftSuccessMessage>
          )}
          {saveDraftFailed && (
            <SaveDraftFailedMessage id="savedraft-failed">
              Failed to save.
            </SaveDraftFailedMessage>
          )}
        </PageDraftButton>
      </PageLayout>
    </Loading>
  );
};
